import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useQueryClient } from '@tanstack/react-query';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { EventCard } from '@/components/events/event-card';
import { EmptyState } from '@/components/ui/empty-state';
import { SkeletonCard } from '@/components/ui/skeleton-card';
import { EventViewModal } from '@/components/events/event-view-modal';
import { ExportProgramacaoSheet } from '@/components/events/export-programacao-sheet';
import { useEvents, type EventWithTasks } from '@/hooks/use-events';
import { useUiStore } from '@/stores/ui-store';
import { fmtMonth, parseDateOnly, todayISO } from '@/lib/dates';
import { ease } from '@/lib/motion';
import { cn } from '@/lib/utils';

export function ProgramacaoPage() {
  const [mode, setMode] = useState<'cards' | 'calendar'>('cards');
  const { data: events = [], isLoading } = useEvents();
  const openEditEvent = useUiStore((s) => s.openEditEvent);
  const openNewEvent = useUiStore((s) => s.openNewEvent);
  const [viewEvent, setViewEvent] = useState<EventWithTasks | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const qc = useQueryClient();

  void qc;

  // Lista mostra só os futuros (hoje em diante), ordenados por data.
  // Os passados só aparecem ao selecioná-los no Calendário.
  const upcoming = useMemo(() => {
    const today = todayISO();
    return events
      .filter((e) => e.event_date >= today)
      .sort((a, b) => a.event_date.localeCompare(b.event_date));
  }, [events]);

  return (
    <div>
      {/* Cabeçalho: título + subtítulo com contagem + exportar */}
      <header className="mb-5 flex items-start justify-between gap-3">
        <div>
          <h1 className="u-h1">Eventos</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {upcoming.length} {upcoming.length === 1 ? 'evento programado' : 'eventos programados'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setExportOpen(true)}
          className="mt-1 inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-foreground transition active:scale-[0.97] hover:bg-muted"
        >
          <Download className="h-3.5 w-3.5" />
          Exportar
        </button>
      </header>

      <SegmentedControl
        value={mode}
        onChange={(v) => setMode(v)}
        options={[
          { value: 'cards', label: 'Lista' },
          { value: 'calendar', label: 'Calendário' },
        ]}
        className="mb-5"
      />

      <AnimatePresence mode="wait">
        {mode === 'cards' ? (
          <motion.div
            key="cards"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.2 }}
          >
            {isLoading ? (
              <div className="flex flex-col gap-3">
                {Array.from({ length: 3 }, (_, i) => (
                  <SkeletonCard key={i} className="h-48" />
                ))}
              </div>
            ) : upcoming.length === 0 ? (
              <EmptyState
                icon={<CalendarIcon className="h-5 w-5" />}
                title="Nenhum evento futuro"
                description="Eventos passados ficam no calendário."
                action={
                  <button
                    onClick={openNewEvent}
                    className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-red transition active:scale-[0.97]"
                  >
                    Criar evento
                  </button>
                }
              />
            ) : (
              // Cards empilhados em coluna, com entrada escalonada
              <motion.div
                initial="hidden"
                animate="show"
                variants={{ hidden: {}, show: { transition: { staggerChildren: 0.05 } } }}
                className="flex flex-col gap-3"
              >
                {upcoming.map((e) => (
                  <motion.div
                    key={e.id}
                    variants={{
                      hidden: { opacity: 0, y: 12 },
                      show: {
                        opacity: 1,
                        y: 0,
                        transition: { duration: 0.35, ease },
                      },
                    }}
                  >
                    <EventCard event={e} onClick={() => setViewEvent(e)} />
                  </motion.div>
                ))}
              </motion.div>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="cal"
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 10 }}
            transition={{ duration: 0.2 }}
          >
            <MonthGrid events={events} onSelectEvent={(e) => setViewEvent(e)} />
          </motion.div>
        )}
      </AnimatePresence>

      <ExportProgramacaoSheet
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        events={events}
      />

      <EventViewModal
        open={!!viewEvent}
        event={viewEvent}
        onClose={() => setViewEvent(null)}
        onEdit={() => {
          const e = viewEvent;
          setViewEvent(null);
          if (e) setTimeout(() => openEditEvent(e), 250);
        }}
      />
    </div>
  );
}

interface MonthGridProps {
  events: EventWithTasks[];
  onSelectEvent: (e: EventWithTasks) => void;
}

function MonthGrid({ events, onSelectEvent }: MonthGridProps) {
  const [monthDate, setMonthDate] = useState(() => {
    const d = parseDateOnly(todayISO());
    d.setDate(1);
    return d;
  });

  // Células do mês (domingo como primeira coluna, igual ao protótipo)
  const cells = useMemo(() => {
    const first = new Date(monthDate);
    first.setDate(1);
    const start = first.getDay();
    const days = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
    const arr: Array<{ date: string | null }> = [];
    for (let i = 0; i < start; i++) arr.push({ date: null });
    for (let d = 1; d <= days; d++) {
      const y = first.getFullYear();
      const m = String(first.getMonth() + 1).padStart(2, '0');
      const day = String(d).padStart(2, '0');
      arr.push({ date: `${y}-${m}-${day}` });
    }
    return arr;
  }, [monthDate]);

  const eventsByDate = useMemo(() => {
    const map = new Map<string, EventWithTasks[]>();
    events.forEach((e) => {
      const arr = map.get(e.event_date) ?? [];
      arr.push(e);
      map.set(e.event_date, arr);
    });
    return map;
  }, [events]);

  const [selected, setSelected] = useState<string | null>(todayISO());
  const selectedEvents = selected ? (eventsByDate.get(selected) ?? []) : [];
  const today = todayISO();

  return (
    <div>
      {/* Navegação de mês: chevrons + título mono uppercase */}
      <div className="mb-4 flex items-center justify-between">
        <button
          onClick={() =>
            setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1))
          }
          className="grid h-9 w-9 place-items-center rounded-md border border-border bg-card text-foreground transition active:scale-[0.97]"
          aria-label="Mês anterior"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <h2 className="font-mono text-[13px] font-semibold uppercase tracking-wider">
          {fmtMonth(monthDate)}
        </h2>
        <button
          onClick={() =>
            setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1))
          }
          className="grid h-9 w-9 place-items-center rounded-md border border-border bg-card text-foreground transition active:scale-[0.97]"
          aria-label="Próximo mês"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Cabeçalho dos dias da semana (domingo primeiro) */}
      <div className="mb-1.5 grid grid-cols-7 gap-1">
        {['d', 's', 't', 'q', 'q', 's', 's'].map((d, i) => (
          <div key={i} className="eyebrow text-center text-[10px]">
            {d}
          </div>
        ))}
      </div>

      {/* Grade de dias */}
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i) => {
          if (!c.date) return <div key={i} className="aspect-square" />;
          const has = eventsByDate.has(c.date);
          const isToday = c.date === today;
          const isSel = selected === c.date;
          const day = parseInt(c.date.slice(-2));
          return (
            <button
              key={i}
              onClick={() => setSelected(c.date)}
              className={cn(
                'relative flex aspect-square flex-col items-center justify-center rounded-md text-sm font-medium transition-colors',
                isSel
                  ? 'bg-foreground text-background'
                  : isToday
                    ? 'border-[1.5px] border-primary text-primary'
                    : 'text-foreground',
              )}
            >
              {day}
              {has && (
                <span
                  className={cn(
                    'absolute bottom-1.5 h-1 w-1 rounded-full',
                    isSel ? 'bg-background' : 'bg-primary',
                  )}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Lista de eventos do dia selecionado */}
      <div className="mt-6">
        <p className="eyebrow mb-3">{selectedEvents.length} evento(s) nesse dia</p>
        <div className="flex flex-col gap-2.5">
          {selectedEvents.length ? (
            selectedEvents.map((e) => (
              <EventCard key={e.id} event={e} onClick={() => onSelectEvent(e)} />
            ))
          ) : (
            <div className="rounded-xl bg-surface-2 px-6 py-6 text-center text-[13px] text-muted-foreground">
              Nada agendado.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
